/**
 * Revisão da extração IA de um PCMSO/PGR — a tela mais importante do app.
 *
 * Todo o estado da tela É o próprio JSON do contrato (ExtracaoPayload).
 * O administrativo corrige o que a IA errou e então:
 *   - "Salvar revisão"       → PATCH /documentos/{id}/extracao (status em_revisao)
 *   - "Aprovar e estruturar" → POST  /documentos/{id}/aprovar (materializa GHEs)
 */
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { apiFetchBlob } from "@/auth/api";
import {
  useAptidoes,
  useAprovarDocumento,
  useAtualizarDocumento,
  useDocumento,
  useSalvarRevisao,
} from "@/hooks/useApi";
import type {
  AptidaoChave,
  Confianca,
  ExtracaoPayload,
  Ghe,
} from "@/types/extracao";
import {
  APTIDAO_ROTULOS,
  GRUPO_RISCO_ROTULOS,
  GRUPOS_RISCO,
  rotuloEscopo,
} from "@/types/extracao";

// A lista vem do catálogo (useAptidoes). Esta ordem só decide a exibição
// quando o catálogo ainda não carregou.
const APTIDOES_FALLBACK: AptidaoChave[] = [
  "trabalho_em_altura",
  "espaco_confinado",
  "maquinas_moveis",
  "protecao_respiratoria",
  "trabalho_com_eletricidade",
];

const CONFIANCA_BADGE: Record<Confianca, string> = {
  alta: "badge bg-gray-100 text-ink-muted",
  media: "badge bg-amber-100 text-amber-800",
  baixa: "badge bg-red-100 text-red-800",
};

export function DocumentoRevisaoPage() {
  const { id } = useParams();
  const documentoId = id ? Number(id) : null;
  const navigate = useNavigate();

  const { data: doc, isLoading } = useDocumento(documentoId);
  const salvar = useSalvarRevisao();
  const salvarEscopo = useAtualizarDocumento();
  const aprovar = useAprovarDocumento();

  const [payload, setPayload] = useState<ExtracaoPayload | null>(null);
  // Escopo NÃO faz parte do JSON da extração: é coluna própria do documento
  // (sobrevive ao reprocessamento da IA) e vai por PATCH /documentos/{id}.
  const [escopo, setEscopo] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [erroPdf, setErroPdf] = useState<string | null>(null);

  const editavel = doc?.status === "rascunho" || doc?.status === "em_revisao";

  // Carrega o JSON UMA vez (não sobrescreve edições em refetch).
  useEffect(() => {
    if (doc?.extracao_ia && payload === null) {
      setPayload(structuredClone(doc.extracao_ia));
    }
  }, [doc, payload]);

  // Escopo salvo no documento (idem: carrega uma vez).
  useEffect(() => {
    if (doc && escopo === null) setEscopo(doc.escopo ?? "");
  }, [doc, escopo]);

  const escopoAlterado = escopo !== null && doc != null && escopo !== (doc.escopo ?? "");
  const temPendencia = dirty || escopoAlterado;

  // Estado sujo avisa antes de fechar/atualizar a aba.
  useEffect(() => {
    if (!dirty && !escopoAlterado) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty, escopoAlterado]);

  function mutate(fn: (draft: ExtracaoPayload) => void) {
    setPayload((prev) => {
      if (!prev) return prev;
      const draft = structuredClone(prev);
      fn(draft);
      return draft;
    });
    setDirty(true);
  }

  async function verPdf() {
    if (documentoId === null) return;
    setErroPdf(null);
    try {
      const { blob } = await apiFetchBlob(`/documentos/${documentoId}/arquivo`);
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener");
    } catch (e) {
      setErroPdf((e as Error).message);
    }
  }

  async function handleSalvar() {
    if (documentoId === null || !payload) return;
    try {
      await salvar.mutateAsync({ id: documentoId, payload });
      if (escopoAlterado) await salvarEscopo.mutateAsync({ id: documentoId, escopo: escopo ?? "" });
      setDirty(false);
    } catch {
      /* erro via salvar.error / salvarEscopo.error */
    }
  }

  async function handleAprovar() {
    if (documentoId === null || !payload) return;
    const confirmado = window.confirm(
      `Aprovar este ${doc?.tipo ?? "documento"} para o escopo "${rotuloEscopo(escopo)}"?\n\n` +
      "As unidades, setores, funções, GHEs, riscos e exigências de exames desta revisão viram a " +
      `base operacional da empresa. O ${doc?.tipo ?? "documento"} aprovado anterior DESTE MESMO ` +
      "escopo será marcado como substituído — documentos de outros escopos/contratos continuam vigentes."
    );
    if (!confirmado) return;
    try {
      // garante que a última edição está gravada antes de materializar
      if (dirty) await salvar.mutateAsync({ id: documentoId, payload });
      // escopo vai no corpo da aprovação (grava antes de materializar)
      await aprovar.mutateAsync({ id: documentoId, escopo: escopo ?? "" });
      setDirty(false);
      navigate("/documentos", {
        state: {
          msg: `${doc?.tipo ?? "Documento"} aprovado para o escopo "${rotuloEscopo(escopo)}" — `
            + "base estruturada gerada com sucesso.",
        },
      });
    } catch {
      /* erro via aprovar.error */
    }
  }

  function voltar() {
    if (temPendencia && !window.confirm("Há alterações não salvas. Sair mesmo assim?")) return;
    navigate("/documentos");
  }

  if (isLoading) return <p className="text-ink-muted">Carregando…</p>;
  if (!doc) return <p className="text-sm text-ink-muted">Documento não encontrado.</p>;
  if (!doc.extracao_ia || !payload) {
    return (
      <div className="space-y-4">
        <h2 className="font-display text-2xl font-semibold">Revisão da extração</h2>
        <div className="card p-6 text-sm text-ink-muted">
          {doc.extracao_status === "processando"
            ? "A extração IA ainda está em andamento — volte em instantes."
            : doc.extracao_status === "erro"
              ? `A extração falhou: ${doc.extracao_erro ?? "erro desconhecido"}. Reprocesse na lista de documentos.`
              : "Este documento ainda não tem extração pra revisar."}
        </div>
        <Link className="btn-secondary text-xs" to="/documentos">← Documentos</Link>
      </div>
    );
  }

  const d = payload.documento;
  const medico = payload.medico_coordenador;

  return (
    <div className="space-y-6 pb-24">
      {/* ---------- Cabeçalho ---------- */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl font-semibold">
            Revisão — {doc.tipo} · {payload.empresa.razao_social}
          </h2>
          <p className="text-sm text-ink-muted">
            CNPJ {payload.empresa.cnpj} · escopo: {rotuloEscopo(escopo)}
            {!editavel ? " · documento já aprovado (somente leitura)" : ""}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button className="btn-secondary text-xs" onClick={verPdf}>Ver PDF original</button>
          <button className="btn-secondary text-xs" onClick={voltar}>← Documentos</button>
        </div>
      </div>
      {erroPdf ? <p className="text-sm text-anarosa">{erroPdf}</p> : null}

      <fieldset disabled={!editavel} className="space-y-6">
        <div className="card p-4">
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <div className="col-span-2">
              <label className="mb-1 block text-xs font-medium text-ink-muted">
                Escopo / lotação (contrato)
              </label>
              <input
                className="input"
                value={escopo ?? ""}
                onChange={(e) => setEscopo(e.target.value)}
                placeholder="Braskem SP — deixe vazio se vale para a empresa toda"
              />
              <p className="mt-1 text-xs text-ink-subtle">
                Vigência é por escopo: aprovar substitui só o documento anterior deste mesmo
                escopo. Outros contratos da empresa seguem vigentes.
              </p>
            </div>
            <div className="col-span-2">
              <label className="mb-1 block text-xs font-medium text-ink-muted">Revisão</label>
              <input className="input" value={d.revisao ?? ""}
                onChange={(e) => mutate((p) => { p.documento.revisao = e.target.value || null; })} />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-ink-muted">Emissão</label>
              <input className="input" type="date" value={d.data_emissao ?? ""}
                onChange={(e) => mutate((p) => { p.documento.data_emissao = e.target.value || null; })} />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-ink-muted">Elaborador</label>
              <input className="input" value={d.elaborador ?? ""}
                onChange={(e) => mutate((p) => { p.documento.elaborador = e.target.value || null; })} />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-ink-muted">Vigência — início</label>
              <input className="input" type="date" value={d.vigencia_inicio ?? ""}
                onChange={(e) => mutate((p) => { p.documento.vigencia_inicio = e.target.value || null; })} />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-ink-muted">Vigência — fim</label>
              <input className="input" type="date" value={d.vigencia_fim ?? ""}
                onChange={(e) => mutate((p) => { p.documento.vigencia_fim = e.target.value || null; })} />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-ink-muted">Médico coordenador</label>
              <input className="input" placeholder="nome" value={medico?.nome ?? ""}
                onChange={(e) => mutate((p) => {
                  p.medico_coordenador = { crm: null, uf_crm: null, ...p.medico_coordenador, nome: e.target.value };
                })} />
            </div>
            <div className="flex gap-2">
              <div className="flex-1">
                <label className="mb-1 block text-xs font-medium text-ink-muted">CRM</label>
                <input className="input" value={medico?.crm ?? ""}
                  onChange={(e) => mutate((p) => {
                    p.medico_coordenador = { nome: "", uf_crm: null, ...p.medico_coordenador, crm: e.target.value || null };
                  })} />
              </div>
              <div className="w-16">
                <label className="mb-1 block text-xs font-medium text-ink-muted">UF</label>
                <input className="input" maxLength={2} value={medico?.uf_crm ?? ""}
                  onChange={(e) => mutate((p) => {
                    p.medico_coordenador = { nome: "", crm: null, ...p.medico_coordenador, uf_crm: e.target.value.toUpperCase() || null };
                  })} />
              </div>
            </div>
          </div>
        </div>

        {/* ---------- Avisos da extração ---------- */}
        {payload.avisos_extracao.length > 0 ? (
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-4">
            <h3 className="font-display text-base font-semibold text-amber-900">
              ⚠ Avisos da extração — confira antes de aprovar
            </h3>
            <ul className="mt-2 space-y-2">
              {payload.avisos_extracao.map((a, i) => (
                <li key={i} className="text-sm text-amber-900">
                  <span className="font-semibold">{a.campo}</span>
                  {a.pagina != null ? <span className="text-amber-700"> (p. {a.pagina})</span> : null}
                  <span> — {a.motivo}</span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {/* ---------- Unidades ---------- */}
        <div className="card p-4">
          <div className="flex items-center justify-between">
            <h3 className="font-display text-base font-semibold">Unidades / locais de lotação</h3>
            {editavel ? (
              <button className="btn-secondary px-2 py-1 text-xs"
                onClick={() => mutate((p) => {
                  p.unidades.push({ nome: "", cnpj_local: null, contratante: null, cidade: null, uf: null });
                })}>
                + unidade
              </button>
            ) : null}
          </div>
          <div className="mt-3 space-y-2">
            {payload.unidades.length === 0 ? (
              <p className="text-sm text-ink-muted">
                Nenhuma unidade extraída — na aprovação será usada a primeira unidade da empresa (ou criada "Geral").
              </p>
            ) : null}
            {payload.unidades.map((u, i) => (
              <div key={i} className="grid grid-cols-2 items-center gap-2 md:grid-cols-6">
                <input className="input col-span-2" placeholder="nome da unidade" value={u.nome}
                  onChange={(e) => mutate((p) => { p.unidades[i].nome = e.target.value; })} />
                <input className="input" placeholder="contratante" value={u.contratante ?? ""}
                  onChange={(e) => mutate((p) => { p.unidades[i].contratante = e.target.value || null; })} />
                <input className="input" placeholder="CNPJ local" value={u.cnpj_local ?? ""}
                  onChange={(e) => mutate((p) => { p.unidades[i].cnpj_local = e.target.value || null; })} />
                <div className="flex gap-2">
                  <input className="input" placeholder="cidade" value={u.cidade ?? ""}
                    onChange={(e) => mutate((p) => { p.unidades[i].cidade = e.target.value || null; })} />
                  <input className="input w-14" placeholder="UF" maxLength={2} value={u.uf ?? ""}
                    onChange={(e) => mutate((p) => { p.unidades[i].uf = e.target.value.toUpperCase() || null; })} />
                </div>
                {editavel ? (
                  <button className="btn-secondary px-2 py-1 text-xs text-anarosa"
                    onClick={() => mutate((p) => { p.unidades.splice(i, 1); })}>
                    remover
                  </button>
                ) : null}
              </div>
            ))}
          </div>
        </div>

        {/* ---------- GHEs ---------- */}
        {payload.ghes.map((ghe, gi) => (
          <GheCard key={gi} ghe={ghe} gi={gi} editavel={!!editavel} mutate={mutate} />
        ))}
      </fieldset>

      {/* ---------- Rodapé fixo ---------- */}
      {editavel ? (
        <div className="fixed inset-x-0 bottom-0 z-10 border-t border-gray-200 bg-white/95 px-6 py-3 shadow-[0_-2px_8px_rgba(0,0,0,0.06)] backdrop-blur">
          <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3">
            <div className="text-xs text-ink-muted">
              {temPendencia ? "Alterações não salvas." : "Tudo salvo."}
              {salvar.error ? (
                <span className="ml-2 text-anarosa">{(salvar.error as Error).message}</span>
              ) : null}
              {salvarEscopo.error ? (
                <span className="ml-2 text-anarosa">{(salvarEscopo.error as Error).message}</span>
              ) : null}
              {aprovar.error ? (
                <span className="ml-2 text-anarosa">{(aprovar.error as Error).message}</span>
              ) : null}
            </div>
            <div className="flex items-center gap-2">
              <button className="btn-secondary"
                disabled={salvar.isPending || salvarEscopo.isPending || !temPendencia}
                onClick={handleSalvar}>
                {salvar.isPending || salvarEscopo.isPending ? "Salvando…" : "Salvar revisão"}
              </button>
              <button className="btn-primary" disabled={aprovar.isPending || salvar.isPending} onClick={handleAprovar}>
                {aprovar.isPending ? "Aprovando…" : "Aprovar e estruturar"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------
// Card de um GHE: riscos + aptidões + exigências + funções vinculadas
// ---------------------------------------------------------------------
function GheCard({ ghe, gi, editavel, mutate }: {
  ghe: Ghe;
  gi: number;
  editavel: boolean;
  mutate: (fn: (draft: ExtracaoPayload) => void) => void;
}) {
  // Catálogo é a fonte das aptidões. Inclui as que o GHE já tem mesmo que
  // sumam do catálogo (item mesclado/renomeado), senão a marcação existente
  // desapareceria da tela sem ninguém ver.
  const { data: catalogo } = useAptidoes();
  const opcoesAptidao: AptidaoChave[] = [
    ...(catalogo?.map((a) => a.chave) ?? APTIDOES_FALLBACK),
    ...ghe.aptidoes_especificas.filter(
      (c) => !(catalogo?.some((a) => a.chave === c) ?? APTIDOES_FALLBACK.includes(c)),
    ),
  ];
  const rotulo = (c: AptidaoChave) =>
    catalogo?.find((a) => a.chave === c)?.nome ?? APTIDAO_ROTULOS[c] ?? c;

  return (
    <div className="card p-4">
      <div className="flex flex-wrap items-center gap-2">
        <input className="input w-32 font-semibold" value={ghe.codigo} placeholder="código"
          onChange={(e) => mutate((p) => { p.ghes[gi].codigo = e.target.value; })} />
        <input className="input max-w-xs" value={ghe.nome ?? ""} placeholder="nome do GHE"
          onChange={(e) => mutate((p) => { p.ghes[gi].nome = e.target.value || null; })} />
        {editavel ? (
          <button className="btn-secondary ml-auto px-2 py-1 text-xs text-anarosa"
            onClick={() => {
              if (window.confirm(`Remover o ${ghe.codigo} inteiro da revisão?`)) {
                mutate((p) => { p.ghes.splice(gi, 1); });
              }
            }}>
            remover GHE
          </button>
        ) : null}
      </div>

      {/* Aptidões específicas */}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <span className="text-xs font-medium uppercase tracking-wider text-ink-muted">Aptidões específicas:</span>
        {opcoesAptidao.map((chave) => {
          const ativa = ghe.aptidoes_especificas.includes(chave);
          return (
            <button
              key={chave}
              type="button"
              disabled={!editavel}
              className={`badge border transition ${
                ativa
                  ? "border-anarosa bg-anarosa-50 text-anarosa-700"
                  : "border-gray-200 bg-white text-ink-subtle"
              }`}
              onClick={() => mutate((p) => {
                const lista = p.ghes[gi].aptidoes_especificas;
                const idx = lista.indexOf(chave);
                if (idx >= 0) lista.splice(idx, 1);
                else lista.push(chave);
              })}
            >
              {ativa ? "✓ " : ""}{rotulo(chave)}
            </button>
          );
        })}
      </div>

      {/* Riscos */}
      <div className="mt-4">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-semibold">Riscos ({ghe.riscos.length})</h4>
          {editavel ? (
            <button className="btn-secondary px-2 py-1 text-xs"
              onClick={() => mutate((p) => {
                p.ghes[gi].riscos.push({ grupo: "fisico", perigo: "", agravos: null });
              })}>
              + risco
            </button>
          ) : null}
        </div>
        <div className="mt-2 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-wider text-ink-muted">
              <tr>
                <th className="px-2 py-1">Grupo</th>
                <th className="px-2 py-1">Perigo</th>
                <th className="px-2 py-1">Possíveis agravos</th>
                <th className="px-2 py-1"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {ghe.riscos.map((r, ri) => (
                <tr key={ri}>
                  <td className="px-2 py-1">
                    <select className="input w-36" value={r.grupo}
                      onChange={(e) => mutate((p) => {
                        p.ghes[gi].riscos[ri].grupo = e.target.value as typeof r.grupo;
                      })}>
                      {GRUPOS_RISCO.map((g) => (
                        <option key={g} value={g}>{GRUPO_RISCO_ROTULOS[g]}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-2 py-1">
                    <input className="input" value={r.perigo}
                      onChange={(e) => mutate((p) => { p.ghes[gi].riscos[ri].perigo = e.target.value; })} />
                  </td>
                  <td className="px-2 py-1">
                    <input className="input" value={r.agravos ?? ""}
                      onChange={(e) => mutate((p) => { p.ghes[gi].riscos[ri].agravos = e.target.value || null; })} />
                  </td>
                  <td className="px-2 py-1 text-right">
                    {editavel ? (
                      <button className="text-xs text-anarosa hover:underline"
                        onClick={() => mutate((p) => { p.ghes[gi].riscos.splice(ri, 1); })}>
                        remover
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Exigências de exames */}
      <div className="mt-4">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-semibold">Exigências de exames ({ghe.exigencias.length})</h4>
          {editavel ? (
            <button className="btn-secondary px-2 py-1 text-xs"
              onClick={() => mutate((p) => {
                p.ghes[gi].exigencias.push({
                  exame: "", codigo_esocial: null, admissional: false, periodicidade_meses: null,
                  retorno_trabalho: false, mudanca_risco: false, demissional: false,
                  sexo: null, faixa_etaria: null, confianca: "alta",
                });
              })}>
              + exame
            </button>
          ) : null}
        </div>
        <div className="mt-2 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-wider text-ink-muted">
              <tr>
                <th className="px-2 py-1">Exame</th>
                <th className="px-2 py-1">eSocial</th>
                <th className="px-2 py-1 text-center" title="Admissional">ADM</th>
                <th className="px-2 py-1" title="Periodicidade em meses (vazio = não periódico)">Period. (meses)</th>
                <th className="px-2 py-1 text-center" title="Retorno ao trabalho">RET</th>
                <th className="px-2 py-1 text-center" title="Mudança de risco">MUD</th>
                <th className="px-2 py-1 text-center" title="Demissional">DEM</th>
                <th className="px-2 py-1">Confiança</th>
                <th className="px-2 py-1"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {ghe.exigencias.map((ex, ei) => (
                <tr key={ei} className={ex.confianca !== "alta" ? "bg-amber-50" : undefined}>
                  <td className="px-2 py-1 min-w-48">
                    <input className="input" value={ex.exame}
                      onChange={(e) => mutate((p) => { p.ghes[gi].exigencias[ei].exame = e.target.value; })} />
                  </td>
                  <td className="px-2 py-1">
                    <input className="input w-20 font-mono text-xs" value={ex.codigo_esocial ?? ""}
                      onChange={(e) => mutate((p) => {
                        p.ghes[gi].exigencias[ei].codigo_esocial = e.target.value || null;
                      })} />
                  </td>
                  <td className="px-2 py-1 text-center">
                    <input type="checkbox" className="h-4 w-4 accent-anarosa" checked={ex.admissional}
                      onChange={(e) => mutate((p) => {
                        p.ghes[gi].exigencias[ei].admissional = e.target.checked;
                      })} />
                  </td>
                  <td className="px-2 py-1">
                    <input type="number" min={1} className="input w-24"
                      value={ex.periodicidade_meses ?? ""}
                      onChange={(e) => mutate((p) => {
                        const v = e.target.value;
                        p.ghes[gi].exigencias[ei].periodicidade_meses = v ? Number(v) : null;
                      })} />
                  </td>
                  <td className="px-2 py-1 text-center">
                    <input type="checkbox" className="h-4 w-4 accent-anarosa" checked={ex.retorno_trabalho}
                      onChange={(e) => mutate((p) => {
                        p.ghes[gi].exigencias[ei].retorno_trabalho = e.target.checked;
                      })} />
                  </td>
                  <td className="px-2 py-1 text-center">
                    <input type="checkbox" className="h-4 w-4 accent-anarosa" checked={ex.mudanca_risco}
                      onChange={(e) => mutate((p) => {
                        p.ghes[gi].exigencias[ei].mudanca_risco = e.target.checked;
                      })} />
                  </td>
                  <td className="px-2 py-1 text-center">
                    <input type="checkbox" className="h-4 w-4 accent-anarosa" checked={ex.demissional}
                      onChange={(e) => mutate((p) => {
                        p.ghes[gi].exigencias[ei].demissional = e.target.checked;
                      })} />
                  </td>
                  <td className="px-2 py-1">
                    <span className={CONFIANCA_BADGE[ex.confianca]}>{ex.confianca}</span>
                  </td>
                  <td className="px-2 py-1 text-right">
                    {editavel ? (
                      <button className="text-xs text-anarosa hover:underline"
                        onClick={() => mutate((p) => { p.ghes[gi].exigencias.splice(ei, 1); })}>
                        remover
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Funções vinculadas */}
      <div className="mt-4">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-semibold">Funções vinculadas ({ghe.funcoes.length})</h4>
          {editavel ? (
            <button className="btn-secondary px-2 py-1 text-xs"
              onClick={() => mutate((p) => {
                p.ghes[gi].funcoes.push({ unidade: null, setor: null, funcao: "" });
              })}>
              + função
            </button>
          ) : null}
        </div>
        <div className="mt-2 space-y-2">
          {ghe.funcoes.map((f, fi) => (
            <div key={fi} className="grid grid-cols-2 items-center gap-2 md:grid-cols-4">
              <input className="input" placeholder="unidade (vazio = padrão)" value={f.unidade ?? ""}
                onChange={(e) => mutate((p) => { p.ghes[gi].funcoes[fi].unidade = e.target.value || null; })} />
              <input className="input" placeholder="setor (vazio = nome do GHE)" value={f.setor ?? ""}
                onChange={(e) => mutate((p) => { p.ghes[gi].funcoes[fi].setor = e.target.value || null; })} />
              <input className="input" placeholder="função" value={f.funcao}
                onChange={(e) => mutate((p) => { p.ghes[gi].funcoes[fi].funcao = e.target.value; })} />
              {editavel ? (
                <button className="btn-secondary w-fit px-2 py-1 text-xs text-anarosa"
                  onClick={() => mutate((p) => { p.ghes[gi].funcoes.splice(fi, 1); })}>
                  remover
                </button>
              ) : null}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
